import styled from 'styled-components';
import {
  InAppContentDefinition,
  InAppDisplaySettingsDefinition,
  MESSAGE_FORMAT,
  Position,
  WebInAppDisplaySettingsDefinition,
} from 'modules/graphql/types';
import { isEqual, pick } from 'lodash';
import { pxToRem } from 'utils/convert';
import { hasHandlebars } from 'modules/templateEditor/common/utils';
import { colors } from 'ui/theme';
import { NegativeFill } from 'ui/icons';

import { IN_APP_DISPLAY, SETTINGS_DISPLAY_CONTENT } from './constants';
import { displayContent } from '../common/content';

const HandlebarsMessageWrapper = styled.div`
  display: grid;
  grid-template-columns: 0fr 1fr;
  grid-gap: ${pxToRem(6)};
`;

export const getMobileInboxRestrictionText = (msg = '', limit: number, isEditMode: boolean) => {
  if (!isEditMode) {
    return '';
  }

  if (hasHandlebars(msg)) {
    return (
      <HandlebarsMessageWrapper>
        <NegativeFill color={colors.textSecondary} />
        <span>{SETTINGS_DISPLAY_CONTENT.handlebarsRestrictionText}</span>
      </HandlebarsMessageWrapper>
    );
  }

  return (
    <>{`${displayContent.mobileInboxStrCharacterLength({
      charCount: msg?.length || 0,
      charLimit: limit || 0,
    })}`}</>
  );
};

// at the moment, the UI shows one "display" option for both
// web in-app and in-app; i.e.: if an in-app is set to "bottom",
// then it will show up in the "BottomRight" for web in-app.
// so we convert the values from inAppSettings into those
// required for web-inapp
export const getPositionFromInAppDisplaySetting = (
  inAppDisplaySetting: InAppDisplaySettingsDefinition,
): Position => {
  const inAppPosition = pick(inAppDisplaySetting, 'top', 'bottom', 'left', 'right');

  return (Object.keys(IN_APP_DISPLAY) as Position[]).find((displayType) => {
    return isEqual(IN_APP_DISPLAY[displayType], inAppPosition);
  }) as Position;
};

/* formatting functions used for the BgOverlayFormSection hex field */
export const formatHexForUI = (hex?: string) => (hex ? hex.slice(1) : '');
export const formatHexForState = (hex: string) => `#${hex}`;
export const formatRokuHexForUI = (hex?: string) => (hex ? hex.slice(2) : '');
export const formatHexForRoku = (hex: string) => `0x${hex}`;

/* formatting functions used for the BgOverlayFormSection opacity field */
export const formatOpacityForUI = (opacity?: number) =>
  opacity !== undefined ? `${Math.round(opacity * 100)}` : '';

export const formatOpacityForState = (opacity: string) => {
  const num = parseInt(opacity, 10);
  if (num < 0 || Number.isNaN(num)) {
    return 0;
  } else if (num > 100) {
    return 1;
  } else {
    return num / 100;
  }
};

export const convertInAppDisplayIntoWebInAppDisplay = (
  inAppDisplaySetting: InAppDisplaySettingsDefinition,
) => {
  const position = getPositionFromInAppDisplaySetting(inAppDisplaySetting);
  switch (position) {
    case Position.Bottom:
      return WebInAppDisplaySettingsDefinition.BottomRight;
    case Position.Center:
      return WebInAppDisplaySettingsDefinition.Center;
    case Position.Full:
      return WebInAppDisplaySettingsDefinition.Full;
    default:
      return WebInAppDisplaySettingsDefinition.TopRight;
  }
};

/**
 * The field "messageFormat" has been added to mediumData for In-App (backend DB). This utility is
 * used within prepInitialData() to give an initial value to messageFormat (when one does not
 * already exist) based on the presence of html and payload values.
 * - For new templates, messageFormat will default to HTML.
 * - For pre-existing templates without a messageFormat value, a determination is made based on html
 *   and payload values
 * IMPORTANT: by using this utility to inject a value into the return from graphql (in cases where
 * messageFormat is not set in the db), we avoid the "There are unsaved changes" notice to the
 * member upon opening the template. Once the template is saved, the calculated value becomes the db
 * value which is used thereafter.
 * UPDATE: the response value for messageFormat has been updated to "Everything" in the BE where one
 * did not previously exist making this check unnecessary
 *
 * @param {InAppContentDefinition} mediumData - the current InApp medium data
 * @returns {MESSAGE_FORMAT} The selected (or calculated) message format of the template
 */
export const getMessageFormat = (mediumData: InAppContentDefinition): MESSAGE_FORMAT => {
  const isUnset = !mediumData.messageFormat;
  if (isUnset) {
    if (!!mediumData?.html?.length && mediumData?.payload) {
      return MESSAGE_FORMAT.Everything;
    }
    if (!mediumData?.html?.length && mediumData?.payload) {
      return MESSAGE_FORMAT.Json;
    }
    return MESSAGE_FORMAT.Everything;
  }
  return mediumData?.messageFormat as MESSAGE_FORMAT;
};

export const getRokuRestrictionText = (msg = '', isEditMode: boolean) => {
  if (!isEditMode) {
    return '';
  }

  if (hasHandlebars(msg)) {
    return (
      <HandlebarsMessageWrapper>
        <NegativeFill color={colors.textSecondary} />
        <span>{SETTINGS_DISPLAY_CONTENT.handlebarsRestrictionText}</span>
      </HandlebarsMessageWrapper>
    );
  }

  return <>{`${displayContent.strCharacterLength({ charCount: msg?.length || 0 })}`}</>;
};
